// File: src/pages/SalesPage.js
// Updated for rich, professional UI with teal (#14b8a6) matching SideNav

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { FaPlus, FaChevronDown, FaChevronRight, FaTrashRestore } from 'react-icons/fa';
import { format, isValid } from 'date-fns';
import toast from 'react-hot-toast';

import SideNav from '../components/SideNav';
import useLicenseCheck from "../hooks/useLicenseCheck";

const teal = '#14b8a6';           // Matches your SideNav logo/active
const tealDark = '#0f9a8a';       // Hover/deeper version
const textDark = '#0f172a';       // Slate-900 – crisp readable text
const textMuted = '#475569';      // Slate-600 – secondary text
const grayLight = '#f8fafc';      // Matches your current bg

const formatCurrency = (amount) => {
  const num = Number(amount) || 0;
  return `GH₵ ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const SalesPage = () => {
useLicenseCheck();

  const navigate = useNavigate();
  const [allSales, setAllSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedRows, setExpandedRows] = useState([]);

  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const [selectedProduct, setSelectedProduct] = useState("");

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/pharmacy/api";
  const getToken = () => localStorage.getItem("token");

  const fetchSales = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        toast.error("No session found. Please login.");
        navigate("/");
        return;
      }

      const res = await axios.get(`${API_BASE_URL}/get_sales.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.status === "success" && Array.isArray(res.data.data)) {
        setAllSales(res.data.data.map(s => ({ ...s, sale_date: new Date(s.sale_date) })));
        setError("");
      } else {
        setAllSales([]);
        setError("No sales available.");
      }
    } catch (err) {
      setAllSales([]);
      setError("Failed to fetch sales.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSales();
  }, []);

  const handleVoidTransaction = (id) => {
    const token = getToken();
    if (!token) return toast.error("Session expired.");

    if (!window.confirm(`Void transaction #${id}? This cannot be undone.`)) return;

    toast.promise(
      axios.post(`${API_BASE_URL}/void_transaction.php`, { transaction_id: id }, {
        headers: { Authorization: `Bearer ${token}` }
      }),
      {
        loading: `Voiding #${id}...`,
        success: (res) => {
          if (res.data.status === 'success') {
            fetchSales();
            return "Transaction voided successfully";
          }
          throw new Error(res.data.message || "Failed");
        },
        error: (err) => `Error: ${err.message}`
      }
    );
  };

  const handleAddSale = () => navigate('/dashboard');

  const uniqueProducts = useMemo(() =>
    [...new Set(allSales.map(s => s.product_name?.trim()).filter(Boolean))].sort(),
  [allSales]);

  const filteredSales = useMemo(() => allSales.filter(s => {
    if (!isValid(s.sale_date)) return false;
    const sd = new Date(startDate);
    const ed = endDate ? new Date(endDate) : null;
    if (ed) ed.setHours(23,59,59,999);
    return (!startDate || s.sale_date >= sd) &&
           (!ed || s.sale_date <= ed) &&
           (!selectedProduct || s.product_name === selectedProduct);
  }), [allSales, startDate, endDate, selectedProduct]);

  const productSummary = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const name = s.product_name || 'Unknown';
      map[name] = map[name] || { name, quantity: 0, totalAmount: 0 };
      map[name].quantity += Number(s.quantity) || 0;
      map[name].totalAmount += Number(s.item_total || s.total || 0);
    });
    return Object.values(map).sort((a,b) => b.totalAmount - a.totalAmount);
  }, [filteredSales]);

  const groupedSales = useMemo(() => {
    const map = {};
    filteredSales.forEach(s => {
      const id = s.transaction_id || s.id;
      if (!map[id]) {
        map[id] = {
          id,
          date: s.sale_date,
          total: Number(s.transaction_total || 0),
          totalQuantity: 0,
          items: [],
          customer: s.customer_name || "Walk-in"
        };
      }
      map[id].items.push(s);
      map[id].totalQuantity += Number(s.quantity) || 0;
      if (!s.transaction_total) map[id].total += Number(s.item_total || s.total || 0);
    });
    return map;
  }, [filteredSales]);

  const transactions = Object.values(groupedSales).sort((a,b) => b.date - a.date);

  const toggleRow = (id) => {
    setExpandedRows(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: grayLight,   // Matches your current light bg
    }}>
      <SideNav activePage="Sales" />

      <div style={{
        flex: 1,
        padding: '2rem 2.5rem',
        backgroundColor: grayLight,
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2.5rem',
        }}>
          <h1 style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: textDark,
            margin: 0,
          }}>
            Sales Overview
          </h1>

          <button
            onClick={handleAddSale}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              backgroundColor: teal,
              color: 'white',
              border: 'none',
              borderRadius: '0.625rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(20,184,166,0.25)',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={e => {
              e.currentTarget.style.backgroundColor = tealDark;
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(20,184,166,0.35)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.backgroundColor = teal;
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(20,184,166,0.25)';
            }}
          >
            <FaPlus size={16} /> New Sale
          </button>
        </div>

        {/* Filters */}
        <div style={{
          background: 'white',
          borderRadius: '0.875rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          padding: '1.75rem',
          marginBottom: '2.5rem',
          border: '1px solid #e5e7eb',
        }}>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '1.75rem',
          }}>
            <div style={{ flex: '1 1 220px' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem', color: textMuted }}>
                From Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s',
                }}
              />
            </div>

            <div style={{ flex: '1 1 220px' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem', color: textMuted }}>
                To Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  transition: 'border-color 0.2s',
                }}
              />
            </div>

            <div style={{ flex: '1 1 220px' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem', color: textMuted }}>
                Product
              </label>
              <select
                value={selectedProduct}
                onChange={e => setSelectedProduct(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: '1px solid #cbd5e1',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  background: 'white',
                  transition: 'border-color 0.2s',
                }}
              >
                <option value="">All Products</option>
                {uniqueProducts.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Product Summary */}
        {!loading && !error && productSummary.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '0.875rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            padding: '1.75rem',
            marginBottom: '2.5rem',
            border: '1px solid #e5e7eb',
          }}>
            <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '1.375rem', fontWeight: 700, color: textDark }}>
              Product Sales Summary
            </h2>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>Product</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'right', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>Qty Sold</th>
                    <th style={{ padding: '1rem 1.5rem', textAlign: 'right', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {productSummary.map((item, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '1.125rem 1.5rem', color: textDark, fontWeight: 500 }}>{item.name}</td>
                      <td style={{ padding: '1.125rem 1.5rem', textAlign: 'right', color: textDark, fontWeight: 600 }}>{item.quantity}</td>
                      <td style={{ padding: '1.125rem 1.5rem', textAlign: 'right', color: tealDark, fontWeight: 700, fontSize: '1.05rem' }}>
                        {formatCurrency(item.totalAmount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Transactions */}
        <div style={{
          background: 'white',
          borderRadius: '0.875rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          border: '1px solid #e5e7eb',
        }}>
          <div style={{
            padding: '1.75rem',
            borderBottom: '1px solid #e5e7eb',
            background: '#f8fafc',
          }}>
            <h2 style={{ margin: 0, fontSize: '1.375rem', fontWeight: 700, color: textDark }}>Transactions</h2>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ width: '60px', padding: '1.125rem', textAlign: 'center' }}></th>
                  <th style={{ padding: '1.125rem', textAlign: 'left', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>ID</th>
                  <th style={{ padding: '1.125rem', textAlign: 'left', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>Customer</th>
                  <th style={{ padding: '1.125rem', textAlign: 'left', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>Date</th>
                  <th style={{ padding: '1.125rem', textAlign: 'right', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>Items</th>
                  <th style={{ padding: '1.125rem', textAlign: 'right', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>Total</th>
                  <th style={{ width: '90px', padding: '1.125rem', textAlign: 'center', fontWeight: 700, color: textMuted, fontSize: '0.875rem', textTransform: 'uppercase' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ padding: '4rem 1rem', textAlign: 'center', color: textMuted, fontSize: '1.1rem' }}>Loading sales data...</td></tr>
                ) : error ? (
                  <tr><td colSpan={7} style={{ padding: '4rem 1rem', textAlign: 'center', color: '#dc2626', fontSize: '1.1rem', fontWeight: 500 }}>{error}</td></tr>
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '4rem 1rem', textAlign: 'center', color: textMuted, fontSize: '1.1rem' }}>No transactions found for the selected filters.</td></tr>
                ) : (
                  transactions.map(tx => (
                    <React.Fragment key={tx.id}>
                      <tr
                        onClick={() => toggleRow(tx.id)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: expandedRows.includes(tx.id) ? '#f0fdfa' : 'white',  // Very light teal tint when expanded
                          transition: 'background-color 0.2s ease',
                        }}
                        onMouseOver={e => { if (!expandedRows.includes(tx.id)) e.currentTarget.style.backgroundColor = '#f0fdfa'; }}
                        onMouseOut={e => { if (!expandedRows.includes(tx.id)) e.currentTarget.style.backgroundColor = 'white'; }}
                      >
                        <td style={{ padding: '1.125rem', textAlign: 'center', color: teal, fontSize: '1.25rem' }}>
                          {expandedRows.includes(tx.id) ? <FaChevronDown /> : <FaChevronRight />}
                        </td>
                        <td style={{ padding: '1.125rem', fontWeight: 600, color: textDark }}>#{tx.id}</td>
                        <td style={{ padding: '1.125rem', color: textDark }}>{tx.customer}</td>
                        <td style={{ padding: '1.125rem', color: textMuted }}>
                          {isValid(tx.date) ? format(tx.date, 'dd MMM yyyy • hh:mm a') : '—'}
                        </td>
                        <td style={{ padding: '1.125rem', textAlign: 'right', color: textDark, fontWeight: 500 }}>{tx.totalQuantity}</td>
                        <td style={{ padding: '1.125rem', textAlign: 'right', color: tealDark, fontWeight: 700, fontSize: '1.1rem' }}>
                          {formatCurrency(tx.total)}
                        </td>
                        <td style={{ padding: '1.125rem', textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleVoidTransaction(tx.id);
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              fontSize: '1.25rem',
                              padding: '0.5rem',
                              transition: 'transform 0.2s',
                            }}
                            onMouseOver={e => e.currentTarget.style.transform = 'scale(1.15)'}
                            onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                            title="Void transaction"
                          >
                            <FaTrashRestore />
                          </button>
                        </td>
                      </tr>

                      {expandedRows.includes(tx.id) && (
                        <tr>
                          <td colSpan={7} style={{ padding: '0 2rem 2rem 2rem', background: '#f0fdfa' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                              <thead>
                                <tr style={{ background: '#ccfbf1' }}>
                                  <th style={{ padding: '0.875rem 1.25rem', textAlign: 'left', fontWeight: 700, color: tealDark }}>Product</th>
                                  <th style={{ padding: '0.875rem 1.25rem', textAlign: 'right', fontWeight: 700, color: tealDark }}>Qty</th>
                                  <th style={{ padding: '0.875rem 1.25rem', textAlign: 'right', fontWeight: 700, color: tealDark }}>Amount</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tx.items.map((item, idx) => (
                                  <tr key={idx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '0.875rem 1.25rem', color: textDark }}>{item.product_name}</td>
                                    <td style={{ padding: '0.875rem 1.25rem', textAlign: 'right', color: textDark, fontWeight: 500 }}>{item.quantity}</td>
                                    <td style={{ padding: '0.875rem 1.25rem', textAlign: 'right', color: tealDark, fontWeight: 600 }}>
                                      {formatCurrency(item.item_total || item.total)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesPage;